import type { ConvexQueryClient } from "@convex-dev/react-query";
import type { QueryClient } from "@tanstack/react-query";
import type { ErrorComponentProps } from "@tanstack/react-router";

import { ClerkProvider, useAuth } from "@clerk/tanstack-react-start";
import { auth } from "@clerk/tanstack-react-start/server";
import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useLocation,
  useRouter,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { createServerFn } from "@tanstack/react-start";
import { ConvexProviderWithClerk } from "convex/react-clerk";

import { Button, buttonVariants } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider, useTheme } from "@/hooks/use-theme";
import { getClerkAppearance } from "@/lib/clerk-appearance";
import { cn } from "@/lib/utils";

import Header from "../components/header";
import appCss from "../index.css?url";
import { useEffect } from "react";

const fetchClerkAuth = createServerFn({ method: "GET" }).handler(async () => {
  const clerkAuth = await auth();
  const token = await clerkAuth.getToken({ template: "convex" });
  return { userId: clerkAuth.userId, token };
});

type ClerkAuthResult = Awaited<ReturnType<typeof fetchClerkAuth>>;

const CLIENT_CLERK_AUTH_CACHE_MS = 25_000;

let clientClerkAuthInflight: Promise<ClerkAuthResult> | null = null;
let clientClerkAuthCache: { until: number; data: ClerkAuthResult } | null = null;

export function invalidateClientClerkAuthCache() {
  clientClerkAuthCache = null;
}

export function primeClientClerkAuthCache(data: ClerkAuthResult) {
  clientClerkAuthCache = { data, until: Date.now() + CLIENT_CLERK_AUTH_CACHE_MS };
}

async function loadClerkAuthForRoot() {
  if (typeof document === "undefined") {
    return fetchClerkAuth();
  }
  const now = Date.now();
  if (clientClerkAuthCache && clientClerkAuthCache.until > now) {
    return clientClerkAuthCache.data;
  }
  if (!clientClerkAuthInflight) {
    clientClerkAuthInflight = (async () => {
      try {
        const data = await fetchClerkAuth();
        clientClerkAuthCache = { data, until: Date.now() + CLIENT_CLERK_AUTH_CACHE_MS };
        return data;
      } finally {
        clientClerkAuthInflight = null;
      }
    })();
  }
  return clientClerkAuthInflight;
}

function extractSubdomain(hostname: string): string | null {
  // Strip a port if present (e.g. "acme.localhost:3001").
  const host = hostname.split(":")[0] ?? hostname;
  // Production: <org>.afinio.co. Dev: <org>.localhost so subdomains can be
  // exercised locally (add `127.0.0.1 acme.localhost` to /etc/hosts).
  const match = host.match(/^([a-z0-9][a-z0-9-]{0,61})\.(?:afinio\.co|localhost)$/i);
  const sub = match?.[1]?.toLowerCase();
  if (!sub || sub === "app" || sub === "www") return null;
  return sub;
}

const getSubdomainFromServer = createServerFn({ method: "GET" }).handler(async () => {
  const { getRequestHost } = await import("@tanstack/react-start/server");
  const host = getRequestHost({ xForwardedHost: true });
  return extractSubdomain(host);
});

async function loadSubdomain(): Promise<string | null> {
  if (typeof document !== "undefined") {
    return extractSubdomain(window.location.hostname);
  }
  return getSubdomainFromServer();
}

const getTheme = createServerFn({ method: "GET" }).handler(async () => {
  const { getCookie } = await import("@tanstack/react-start/server");
  const theme = getCookie("theme");
  return theme === "light" || theme === "dark" ? theme : "dark";
});

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const PRIMARY_APP_ORIGIN = "https://app.afinio.co";

export interface RouterAppContext {
  queryClient: QueryClient;
  convexQueryClient: ConvexQueryClient;
}

function DefaultErrorComponent({ error, reset }: ErrorComponentProps) {
  const detail =
    error.message === "Too Many Requests"
      ? "The sign-in service received too many requests. Wait a few seconds and try again."
      : error.message || "An unexpected error occurred";
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6">
      <h1 className="text-3xl font-bold">Something went wrong</h1>
      <p className="text-muted-foreground text-center text-base">{detail}</p>
      <Button onClick={reset}>Try again</Button>
    </div>
  );
}

function DefaultNotFoundComponent() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6">
      <h1 className="text-3xl font-bold">Page not found</h1>
      <p className="text-muted-foreground text-base">The page you're looking for doesn't exist.</p>
      <Link to="/" className={cn(buttonVariants())}>
        Go home
      </Link>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: "Afinio",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  component: RootDocument,
  errorComponent: DefaultErrorComponent,
  notFoundComponent: DefaultNotFoundComponent,
  beforeLoad: async (ctx) => {
    let theme: "light" | "dark" = "dark";
    if (typeof document !== "undefined") {
      const match = document.cookie.match(/(?:^|; )theme=(light|dark)/);
      theme = (match?.[1] as "light" | "dark") ?? "dark";
    } else {
      theme = await getTheme();
    }

    const [{ userId, token }, subdomain] = await Promise.all([
      loadClerkAuthForRoot(),
      loadSubdomain(),
    ]);
    if (token) {
      ctx.context.convexQueryClient.serverHttpClient?.setAuth(token);
    }

    return { userId, token, theme, subdomain };
  },
});

function RootDocument() {
  const context = Route.useRouteContext();
  // ThemeProvider wraps Clerk so the Clerk appearance can react to the current
  // theme (see ClerkRoot). ThemeProvider is pure context with no Clerk/Convex
  // dependency, so it is safe as the outermost provider.
  return (
    <ThemeProvider initialTheme={context.theme}>
      <ClerkRoot subdomain={context.subdomain} convexQueryClient={context.convexQueryClient} />
    </ThemeProvider>
  );
}

function ClerkRoot({
  subdomain,
  convexQueryClient,
}: {
  subdomain: string | null | undefined;
  convexQueryClient: ConvexQueryClient;
}) {
  const { theme } = useTheme();
  const appearance = getClerkAppearance(theme);
  const children = (
    <ConvexProviderWithClerk client={convexQueryClient.convexClient} useAuth={useAuth}>
      <RootLayout />
    </ConvexProviderWithClerk>
  );

  // Subdomains (<community>.afinio.co) are NOT Clerk satellite domains — they
  // share the same root domain as the primary app, so Clerk's production session
  // cookie (set on .afinio.co) is shared across all subdomains automatically.
  // We only redirect sign-in/sign-up to the primary domain to centralize auth.
  if (subdomain) {
    return (
      <ClerkProvider
        publishableKey={publishableKey}
        appearance={appearance}
        localization={{ formFieldInputPlaceholder__emailAddress: "Enter your work email address" }}
        signInUrl={`${PRIMARY_APP_ORIGIN}/sign-in`}
        // Org subdomains have no sign-up; org creation lives only on the primary
        // app origin. Point Clerk's sign-up at sign-in so it never offers create here.
        signUpUrl={`${PRIMARY_APP_ORIGIN}/sign-in`}
        allowedRedirectOrigins={["https://*.afinio.co", "https://afinio.co"]}
      >
        {children}
      </ClerkProvider>
    );
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      appearance={appearance}
      localization={{ formFieldInputPlaceholder__emailAddress: "Enter your work email address" }}
      allowedRedirectOrigins={["https://*.afinio.co", "https://afinio.co"]}
    >
      {children}
    </ClerkProvider>
  );
}

function RootLayout() {
  const location = useLocation();
  const { theme } = useTheme();
  const router = useRouter();
  const isAppRoute = location.pathname.startsWith("/app");

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        invalidateClientClerkAuthCache();
        void router.invalidate();
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [router]);

  return (
    <html lang="en" data-theme={theme} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <div className={isAppRoute ? "h-svh" : "grid h-svh grid-rows-[auto_1fr]"}>
          {!isAppRoute && <Header />}
          <Outlet />
        </div>
        <Toaster richColors />
        <TanStackRouterDevtools position="bottom-left" />
        <Scripts />
      </body>
    </html>
  );
}
